import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Printer, Copy, Edit3, Share2, EyeOff, Trash2, Sparkles, Globe2, PenSquare, Plus, X, ChevronUp, ChevronDown } from "lucide-react";

const SOURCE_META = {
  rally_lab: { label: "Rally Lab Official", color: "#CCFF00", icon: Sparkles },
  coach_shared: { label: "Coach-shared", color: "#00BFFF", icon: Globe2 },
  coach_draft: { label: "Draft (only you)", color: "#F59E0B", icon: PenSquare },
};

const CONTEXTS = [
  { k: "warmup", label: "Warm-up" },
  { k: "pre_match", label: "Pre-match" },
  { k: "session", label: "Full session" },
  { k: "cool_down", label: "Cool-down" },
];

export default function RoutineDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isNew = id === "new";

  const [routine, setRoutine] = useState(null);
  const [editing, setEditing] = useState(isNew);
  const [form, setForm] = useState(null);
  const [availableDrills, setAvailableDrills] = useState([]);
  const [saving, setSaving] = useState(false);

  // Same fix as DrillDetail: reset editing when navigating to a new routine
  // so the fresh-save redirect flips into the read-only view.
  useEffect(() => { setEditing(id === "new"); }, [id]);

  useEffect(() => {
    if (isNew) {
      setForm({ title: "", sport: "pickleball", description: "", context: "session", drills: [] });
      return;
    }
    api.get(`/routines/${id}`).then((r) => {
      setRoutine(r.data);
      setForm({
        title: r.data.title,
        sport: r.data.sport,
        description: r.data.description || "",
        context: r.data.context,
        drills: r.data.drills || [],
      });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load routine"));
  }, [id, isNew]);

  // Fetch drills matching the sport for the add-picker.
  useEffect(() => {
    if (!form?.sport) return;
    api.get("/drills", { params: { sport: form.sport } }).then((r) => setAvailableDrills(r.data || [])).catch(() => {});
  }, [form?.sport]);

  const canEdit = routine && (
    (routine.source === "rally_lab" && (user?.permissions || []).includes("super_admin")) ||
    (routine.source !== "rally_lab" && routine.author_id === user?.id) ||
    (user?.permissions || []).includes("admin")
  );

  const save = async () => {
    if (!form?.title?.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isNew) {
        const r = await api.post("/routines", payload);
        toast.success("Routine created — saved as a draft");
        nav(`/routines/${r.data.id}`, { replace: true });
      } else {
        await api.patch(`/routines/${id}`, payload);
        const r = await api.get(`/routines/${id}`);
        setRoutine(r.data);
        setEditing(false);
        toast.success("Routine updated");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const addDrillToRoutine = (drillId) => {
    if (form.drills.some((d) => d.drill_id === drillId)) return;
    setForm({ ...form, drills: [...form.drills, { drill_id: drillId, order: form.drills.length, duration_override_min: null }] });
  };
  const removeDrill = (drillId) => {
    setForm({ ...form, drills: form.drills.filter((d) => d.drill_id !== drillId).map((d, i) => ({ ...d, order: i })) });
  };
  const moveDrill = (drillId, dir) => {
    const idx = form.drills.findIndex((d) => d.drill_id === drillId);
    const swap = idx + dir;
    if (swap < 0 || swap >= form.drills.length) return;
    const next = [...form.drills];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setForm({ ...form, drills: next.map((d, i) => ({ ...d, order: i })) });
  };

  const publish = async () => { try { await api.post(`/routines/${id}/publish`); toast.success("Routine shared"); const r = await api.get(`/routines/${id}`); setRoutine(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unpublish = async () => { try { await api.post(`/routines/${id}/unpublish`); toast.success("Routine unshared"); const r = await api.get(`/routines/${id}`); setRoutine(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const clone = async () => { try { const r = await api.post(`/routines/${id}/clone`); toast.success("Cloned to your drafts"); nav(`/routines/${r.data.id}`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async () => { if (!window.confirm("Delete this routine?")) return; try { await api.delete(`/routines/${id}`); toast.success("Deleted"); nav("/drills"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (isNew && !form) return null;
  if (!isNew && !routine) return <div className="pt-14 p-6 text-sm text-[#A3A3A3]">Loading…</div>;

  const meta = routine && SOURCE_META[routine.source];
  const Icon = meta?.icon;
  const totalMin = routine?.drills_expanded?.reduce((s, it) => s + (it.duration_override_min ?? it.drill.duration_min ?? 0), 0) || 0;

  // ------- EDIT VIEW -------
  if (editing && form) {
    const chosenIds = new Set(form.drills.map((d) => d.drill_id));
    const chosen = form.drills
      .map((d) => ({ ...d, drill: availableDrills.find((x) => x.id === d.drill_id) }))
      .filter((d) => d.drill);
    const remaining = availableDrills.filter((d) => !chosenIds.has(d.id));
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white pt-14 pb-16">
        <div className="max-w-3xl mx-auto px-6 space-y-5">
          <Link to="/drills" className="inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-[#CCFF00]"><ArrowLeft className="w-3 h-3" /> Library</Link>
          <h1 className="h-display text-3xl">{isNew ? "New routine" : "Edit routine"}</h1>

          <div className="space-y-4">
            <div>
              <div className="label-mono mb-1 text-[10px]">TITLE *</div>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="routine-title" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="label-mono mb-1 text-[10px]">SPORT</div>
                <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value, drills: [] })} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="routine-sport">
                  <option value="pickleball">Pickleball</option>
                  <option value="squash">Squash</option>
                </select>
              </div>
              <div>
                <div className="label-mono mb-1 text-[10px]">CONTEXT</div>
                <select value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="routine-context">
                  {CONTEXTS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <div className="label-mono mb-1 text-[10px]">DESCRIPTION</div>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="routine-description" />
            </div>

            <div>
              <div className="label-mono mb-2 text-[10px]">DRILLS IN THIS ROUTINE ({chosen.length})</div>
              {chosen.length === 0 && <div className="text-xs text-[#A3A3A3] italic mb-2">Add drills below.</div>}
              <div className="space-y-1" data-testid="routine-drills">
                {chosen.map((item, i) => (
                  <div key={item.drill_id} className="flex items-center gap-2 border border-[#262626] px-3 py-2" data-testid={`routine-item-${item.drill_id}`}>
                    <div className="text-xs text-[#A3A3A3] label-mono w-6">{i + 1}.</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{item.drill.title}</div>
                      <div className="text-[10px] text-[#A3A3A3]">{item.drill.duration_min} min · {(item.drill.sub_skills || []).slice(0, 3).join(", ")}</div>
                    </div>
                    <button onClick={() => moveDrill(item.drill_id, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" data-testid={`up-${item.drill_id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveDrill(item.drill_id, 1)} disabled={i === chosen.length - 1} className="p-1 disabled:opacity-30" data-testid={`down-${item.drill_id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeDrill(item.drill_id)} className="p-1 text-[#FF4500]" data-testid={`remove-${item.drill_id}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              {remaining.length > 0 && (
                <div className="mt-3">
                  <div className="label-mono mb-1 text-[10px] text-[#A3A3A3]">ADD DRILL</div>
                  <select onChange={(e) => { if (e.target.value) { addDrillToRoutine(e.target.value); e.target.value = ""; } }} className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm" data-testid="add-drill-select">
                    <option value="">— pick one to add —</option>
                    {remaining.map((d) => (
                      <option key={d.id} value={d.id}>[{d.source}] {d.title}  ·  {d.duration_min}m</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} className="btn-volt px-6 py-2 text-xs disabled:opacity-50" data-testid="routine-save">
              {saving ? "Saving…" : (isNew ? "Create routine" : "Save changes")}
            </button>
            {!isNew && <button onClick={() => setEditing(false)} className="px-4 py-2 text-xs text-[#A3A3A3]">Cancel</button>}
          </div>
        </div>
      </div>
    );
  }

  // ------- READ / PRINT VIEW -------
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pt-14 pb-16 print:bg-white print:text-black print:pt-2">
      <div className="max-w-3xl mx-auto px-6 space-y-6 print:space-y-3">
        <div className="print:hidden">
          <Link to="/drills" className="inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-[#CCFF00]" data-testid="back-to-library"><ArrowLeft className="w-3 h-3" /> Library</Link>
        </div>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {meta && (
                <span className="text-[9px] uppercase tracking-wider label-mono px-1.5 py-0.5 print:hidden" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1 }} data-testid="routine-source-badge">
                  <Icon className="w-2.5 h-2.5 inline mr-0.5" /> {meta.label}
                </span>
              )}
              <span className="text-[10px] text-[#A3A3A3] label-mono print:text-black">
                {routine.sport} · {(CONTEXTS.find((c) => c.k === routine.context) || {}).label || routine.context} · ~{totalMin} min · {routine.drills_expanded?.length || 0} drills
              </span>
            </div>
            <h1 className="h-display text-3xl print:text-2xl" data-testid="routine-title-view">{routine.title}</h1>
            {routine.description && <p className="text-sm text-[#A3A3A3] mt-2 print:text-black">{routine.description}</p>}
          </div>
          <div className="flex gap-2 flex-wrap print:hidden">
            <button onClick={() => window.print()} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5" data-testid="print-routine">
              <Printer className="w-3.5 h-3.5" /> Print / PDF
            </button>
            <button onClick={clone} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5" data-testid="clone-routine">
              <Copy className="w-3.5 h-3.5" /> Clone
            </button>
            {canEdit && <button onClick={() => setEditing(true)} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] inline-flex items-center gap-1.5" data-testid="edit-routine"><Edit3 className="w-3.5 h-3.5" /> Edit</button>}
            {canEdit && routine.source === "coach_draft" && <button onClick={publish} className="px-3 py-2 text-xs uppercase tracking-wider bg-[#00BFFF] text-black inline-flex items-center gap-1.5" data-testid="publish-routine"><Share2 className="w-3.5 h-3.5" /> Share</button>}
            {canEdit && routine.source === "coach_shared" && <button onClick={unpublish} className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] inline-flex items-center gap-1.5" data-testid="unpublish-routine"><EyeOff className="w-3.5 h-3.5" /> Unshare</button>}
            {canEdit && routine.source !== "rally_lab" && <button onClick={del} className="px-3 py-2 text-xs uppercase tracking-wider text-[#FF4500] inline-flex items-center gap-1.5" data-testid="delete-routine"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
          </div>
        </div>

        {/* Ordered list of drills — each with its full detail so a printed
            copy is court-side usable end-to-end. */}
        <div className="space-y-6 print:space-y-4">
          {(routine.drills_expanded || []).map((item, i) => {
            const d = item.drill;
            const dur = item.duration_override_min ?? d.duration_min;
            return (
              <div key={d.id} className="print:break-inside-avoid border-l-4 border-[#CCFF00] pl-4 print:border-black" data-testid={`routine-drill-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-display text-4xl text-[#CCFF00] print:text-black" data-testid={`routine-drill-num-${d.id}`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Link to={`/drills/${d.id}`} className="text-xl text-white hover:text-[#CCFF00] print:text-black print:no-underline" data-testid={`routine-drill-title-${d.id}`}>{d.title}</Link>
                      <span className="text-[10px] text-[#A3A3A3] label-mono print:text-black">{dur} min · L{d.skill_level_min}-{d.skill_level_max}</span>
                    </div>
                    {d.summary && <p className="text-sm text-[#A3A3A3] mt-1 print:text-black">{d.summary}</p>}
                    {d.description && <p className="text-xs leading-relaxed mt-2 whitespace-pre-line text-[#E5E5E5] print:text-black">{d.description}</p>}
                    {d.court_setup && (
                      <div className="mt-2 text-[10px] print:text-black">
                        <span className="label-mono text-[#CCFF00] print:text-black">SETUP: </span>
                        <span className="text-[#A3A3A3] print:text-black">{d.court_setup}</span>
                      </div>
                    )}
                    {d.coaching_cues?.length > 0 && (
                      <ul className="mt-2 pl-4 list-disc text-xs space-y-0.5 print:text-black">
                        {d.coaching_cues.map((c, ci) => <li key={ci}>{c}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {routine.drills_expanded?.length === 0 && (
          <div className="card-surface p-8 text-center text-sm text-[#A3A3A3] print:hidden">
            No drills yet — <button onClick={() => setEditing(true)} className="text-[#CCFF00] underline">edit routine</button> to add some.
          </div>
        )}

        <div className="text-[10px] text-[#A3A3A3] label-mono pt-4 border-t border-[#262626] print:text-black print:border-black">
          {routine.author_name || "Rally Lab"} · Last updated {(routine.updated_at || routine.created_at || "").slice(0, 10)}
        </div>
      </div>
    </div>
  );
}
